import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import axios from 'axios';
import JobListings from './components/JobListings';
import JobFilters from './components/JobFilters';
import LandingPage from './components/LandingPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import Terms from './components/Terms.jsx';
import Pricing from './components/Pricing.jsx';
import Blogs from './components/Blogs.jsx';
import SignInPage from './components/auth/SignIn.jsx';
import SignOut from './components/auth/SignOut.jsx';
import DashBoard from './components/DashBoard';
import SearchBar from './components/SearchBar';

import './App.css';

// Get Clferk publishable key
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  console.error('Clerk publishable key is missing. Please check your environment variables.');
  throw new Error('Clerk publishable key is required');
}

// Safely access environment variables with fallback
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    console.log("Using CRA env var");
    return process.env.REACT_APP_API_URL;
  }

  console.warn('API URL not found in environment, using hardcoded fallback');
  return 'https://algoheads-com.onrender.com';
};

const apiUrl = getApiUrl();
console.log('Using API URL:', apiUrl);

// Separate AppContent component that uses hooks
function AppContent() {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchJobs = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      console.log("Starting job fetch with URL:", `${apiUrl}/jobs`);

      const response = await axios.get(`${apiUrl}/jobs`, {
        params: {
          limit: 5000
        }
      });

      console.log("Raw response:", response.data);
      if (response.data && response.data.jobs) {
        console.log(`Received ${response.data.jobs.length} jobs from backend`);
        setJobs(response.data.jobs);
        if (isFiltered) {
          setIsFiltered(false);
          setFilteredJobs([]);
        }
      } else {
        console.warn("No jobs array in response:", response.data);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
      }
      // setError('Failed to fetch jobs');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [apiUrl]);

  const handleUploadComplete = async () => {
    console.log("Upload complete, fetching jobs...");
    await fetchJobs(false);
    navigate('/jobs');
  };

  const handleFilterApplied = (filteredJobsData) => {
    if (filteredJobsData === null) {
      setIsFiltered(false);
      setFilteredJobs([]);
    } else {
      setFilteredJobs(filteredJobsData);
      setIsFiltered(true);
    }
  };

  useEffect(() => {
    if (error) {
      // Handle error if needed
    }
  }, [error]);

  useEffect(() => {
    fetchJobs(false);
    
    let pollInterval;
    if (location.pathname === '/jobs') {
      pollInterval = setInterval(() => {
        fetchJobs(true);
      }, 3000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [location.pathname, fetchJobs]);

  const handleLogoClick = async (e) => {
    e.preventDefault();

    // const confirmReset = window.confirm('Do you want to clear all jobs data? Click OK to reset, or Cancel to just go to home page.');
     
    //  if (confirmReset) {
    //    try {
    //      await axios.post(`${apiUrl}/reset`);
    //      setJobs([]);
    //      setFilteredJobs([]);
    //      setIsFiltered(false);
    //      console.log('Database reset successful');
    //    } catch (err) {
    //      console.error('Error resetting database:', err);
    //    }
    //  }
    
    
    setJobs([]);
    setFilteredJobs([]);
    setIsFiltered(false);
    setError(null);
    navigate('/');
  };

  return (
    <div className="app-container">
      <div className="app-content">
        <Navbar
          jobsCount={isFiltered ? filteredJobs.length : jobs.length}
          onLogoClick={handleLogoClick}
        />
        {error && (
          <div className="max-w-7xl mx-auto mt-2 p-2 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}
        <main className="main-content">
          <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
            <Routes>
              {/* Public Routes */}
              <Route path="/sign-in/*" element={<SignInPage />} />
              <Route path="/sign-up/*" element={<SignInPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/blogs" element={<Blogs />} />
              
              {/* Dashboard Route - Protected */}
              <Route
                path="/dashboard"
                element={
                  <>
                    <SignedIn>
                      <DashBoard />
                    </SignedIn>
                    <SignedOut>
                      <LandingPage onUploadComplete={handleUploadComplete} apiUrl={apiUrl} />
                    </SignedOut>
                  </>
                }
              />
              
              {/* Landing Page - accessible to all but with conditional content */}
              <Route
                path="/"
                element={<LandingPage onUploadComplete={handleUploadComplete} apiUrl={apiUrl} />}
              />

              {/* Protected Route */}
              <Route
                path="/jobs"
                element={
                  <>
                    <SignedIn>
                      <div className="space-y-6">
                        <div className="search-filters-container">
                          <SearchBar 
                            jobs={isFiltered ? filteredJobs : jobs} 
                            onSearchResults={setFilteredJobs} 
                          />
                          <JobFilters 
                            apiUrl={apiUrl} 
                            onFilterApplied={handleFilterApplied} 
                          />
                        </div>
                        
                        <JobListings 
                          jobs={isFiltered ? filteredJobs : jobs} 
                          loading={loading} 
                        />
                      </div>
                    </SignedIn>
                    <SignedOut>
                      <LandingPage onUploadComplete={handleUploadComplete} apiUrl={apiUrl} />
                    </SignedOut>
                  </>
                }
              />
            </Routes>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

// Main App component that provides router context
function App() {
  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <Router>
        <AppContent />
      </Router>
    </ClerkProvider>
  );
}

export default App;
